import type { Item } from "gamedata/GameData";
import { select } from "gamedata/GameData";
import type { ReactNode } from "react";
import { useState } from "react";
import { Link } from "react-router";
import { z } from "zod";
import { CheckBox } from "~/components/form/CheckBox";
import { ControlBox } from "~/components/form/ControlBox";
import { MultiSelect } from "~/components/form/MultiSelect";
import { AdenaValue } from "~/components/item/Adena";
import { CrystalColor } from "~/components/item/Crystal";
import { ItemGrade } from "~/components/item/Grade";
import { MENU_MAP } from "~/components/site/Header";
import { EntityTooltip } from "~/components/site/Tooltip";
import { Breadcrumb } from "~/components/ui/Breadcrumb";
import { Button } from "~/components/ui/Button";
import { Icon32 } from "~/components/ui/Icon";
import { PageTitle } from "~/components/ui/PageTitle";
import { Table } from "~/components/ui/Table";
import { GD_ALL } from "~/gd";
import {
  ARMOR_TYPE_NAMES,
  ARMOR_TYPES_BY_GAME_VER,
  ArmorType,
  Grade,
  GRADE_NAMES,
  GRADES,
  Slot,
  SLOT_NAMES,
  SLOTS_BY_GAME_VER,
  WEAPON_TYPE_NAMES,
  WEAPON_TYPES_BY_GAME_VER,
  WeaponType,
} from "~/types";
import { gameName } from "~/util/common";
import { createFiltering } from "~/util/Filtering";
import { lstring, tt, useRealm, useT, useTurl } from "~/util/Lang";
import { applyPaging, Pager } from "~/util/Listing";
import { $LOADER, $PAGE, MetaArgs, type OurLoaderArgs } from "~/util/Loaders";
import { createSorting, SortColumnName, useUrlSorting } from "~/util/Sorting";
import { logPageView } from "~/util/stats";
import { homePage } from "~/util/titles";
import {
  urlQueryFromSearchParams,
  useSetUrlQuery,
  useUrlQuery,
} from "~/util/UrlUtils";

const PageQuery = z.object({
  p: z.optional(z.string()),
  s: z.optional(z.string()),
});

type Config = {
  noArmorType?: boolean;
  noWeaponType?: boolean;
  filter?: (item: Item) => boolean;
};

type RenderConfig = {
  title: lstring;
  menuItemId: string;

  weaponStats?: boolean;
  shieldStats?: boolean;
  armorStats?: boolean;
  jewelryStats?: boolean;
};

type ItemFilter = {
  grade: readonly Grade[];
  slot: readonly Slot[];
  weap_type: readonly WeaponType[];
  armr_type: readonly ArmorType[];
  canGet: null | boolean;
};

const SORTER = createSorting<Item>({
  pAtk: (a, b) => (a.physicalDamage ?? 0) - (b.physicalDamage ?? 0),
  mAtk: (a, b) => (a.magicalDamage ?? 0) - (b.magicalDamage ?? 0),
  pDef: (a, b) => (a.physicalDefense ?? 0) - (b.physicalDefense ?? 0),
  shieldDefense: (a, b) => (a.shieldDefense ?? 0) - (b.shieldDefense ?? 0),
  mDef: (a, b) => (a.magicalDefense ?? 0) - (b.magicalDefense ?? 0),
  cryCount: (a, b) => (a.crystalCount ?? 0) - (b.crystalCount ?? 0),
});

const FILTER = createFiltering<ItemFilter, Item>({
  pack: (f) => ({
    grade: f.grade?.join(","),
    wt: f.weap_type.join(","),
    at: f.armr_type.join(","),
    slot: f.slot.join(","),
    canGet:
      f.canGet != null && f.canGet != false ? f.canGet.toString() : undefined,
  }),
  unpack: (r) => {
    const grade = Grade.array().parse(r.grade ? r.grade.split(",") : []);
    const weap_type = WeaponType.array().parse(r.wt?.split(",") ?? []);
    const armr_type = ArmorType.array().parse(r.at?.split(",") ?? []);
    const slot = Slot.array().parse(r.slot?.split(",") ?? []);
    const canGet = r.canGet ? true : null;
    return {
      grade,
      weap_type,
      armr_type,
      slot,
      canGet,
    };
  },
  filter: (item, f) => {
    if (f.grade.length > 0 && !f.grade.includes(item.crystalType as any)) {
      return false;
    }
    if (f.weap_type.length > 0 && !f.weap_type.includes(item.weaponType)) {
      return false;
    }
    if (f.armr_type.length > 0 && !f.armr_type.includes(item.armorType)) {
      return false;
    }
    if (f.slot.length > 0 && !f.slot.includes(item.slotBitType)) {
      return false;
    }
    if (
      f.canGet &&
      !item.dropList.length &&
      !item.spoilList.length &&
      // !item.product.length &&
      !item.sellList.length &&
      !item.exchange_from.length &&
      !item.questReward.length
    ) {
      return false;
    }
    return true;
  },
});

async function makeData(la: OurLoaderArgs, cfg: Config) {
  // Log
  logPageView(la);
  //
  const query = PageQuery.parse(
    Object.fromEntries(la.url.searchParams.entries())
  );
  const filter = FILTER.fromUrlQuery(
    urlQueryFromSearchParams(la.url.searchParams)
  );

  const all = Array.from(GD_ALL[la.realm.game].items.values());
  const sorted = SORTER.apply(all, query.s);
  const filtered = FILTER.apply(sorted, filter, cfg.filter);
  const [paged, paging] = applyPaging(filtered, query);

  const items = select(paged, {
    select: {
      id: true,
      icon: true,
      itemName: true,
      name: (name) => tt(la.realm.lang, name),
      addName: (name) => tt(la.realm.lang, name),
      crystalType: true,

      weaponType: true,
      physicalDamage: true,
      magicalDamage: true,
      soulshotCount: true,
      spiritshotCount: true,
      attackSpeed: true,

      armorType: true,
      slotBitType: true,
      physicalDefense: true,
      magicalDefense: true,
      shieldDefense: true,

      weight: true,
      defaultPrice: true,
      crystalCount: true,
    },
  });

  return { items, paging, authUser: la.req.sess?.user };
}

type LoaderData = Awaited<ReturnType<typeof makeData>>;

function makeMeta(
  cx: MetaArgs<LoaderData>,
  opts: { title: lstring; canonicalPath?: string }
) {
  const { data } = cx;
  let title = cx.t(opts.title);
  if (data.paging.page > 1) {
    title += ` (${cx.t({ en: "Page", ru: "Страница" })} ${data.paging.page})`;
  }

  return {
    title: `${title} - ${homePage(cx.realm)}`,
    description: cx.t({
      en: `A complete searchable and filterable list of all Items in ${gameName(
        cx.realm.game
      )}.`,
      ru: `Полный список всех предметов ${gameName(
        cx.realm.game
      )} с возможностью фильтрации, Оружие, Броня, Бижутерия, Квестовые предметы, Улучшенные предметы и многое другое.`,
    }),
    ogTitle: cx.t({
      en: `${opts.title.en} ${gameName(cx.realm.game)}`,
      ru: `${opts.title.ru} ${gameName(cx.realm.game)}`,
    }),
    canonicalUrl: !opts.canonicalPath ? `/items` : opts.canonicalPath,
  };
}

$PAGE({
  path: "items",
  loader: $LOADER?.((la) => {
    return makeData(la, {});
  }),
  meta: (cx) => makeMeta(cx, { title: { en: "Items", ru: "Предметы" } }),
  render: ({ data }) => {
    return (
      <ItemsPage
        config={{
          title: { en: "Item", ru: "Предметы" },
          menuItemId: "items",
          weaponStats: true,
        }}
        data={data}
      />
    );
  },
});

$PAGE({
  path: "items/weapon",
  loader: $LOADER?.((la) => {
    return makeData(la, {
      filter: (item) => item.weaponType != "none" && (item.weight ?? 0) > 100,
    });
  }),
  meta: (cx) =>
    makeMeta(cx, {
      title: { en: "Weapons", ru: "Оружие" },
      canonicalPath: "/items/weapon",
    }),
  render: ({ data }) => {
    return (
      <ItemsPage
        config={{
          title: { en: "Weapons", ru: "Оружие" },
          menuItemId: "weapon",
          weaponStats: true,
        }}
        data={data}
      />
    );
  },
});

$PAGE({
  path: "items/armor",
  loader: $LOADER?.((la) => {
    return makeData(la, {
      filter: (item) =>
        item.slotBitType == "chest" ||
        item.slotBitType == "gloves" ||
        item.slotBitType == "head" ||
        item.slotBitType == "feet" ||
        item.slotBitType == "legs" ||
        item.slotBitType == "onepiece",
    });
  }),
  meta: (cx) =>
    makeMeta(cx, {
      title: { en: "Armor", ru: "Броня" },
      canonicalPath: "/items/armor",
    }),
  render: ({ data }) => {
    return (
      <ItemsPage
        config={{
          title: { en: "Armor", ru: "Броня" },
          menuItemId: "armor",
          armorStats: true,
        }}
        data={data}
      />
    );
  },
});

$PAGE({
  path: "items/shield",
  loader: $LOADER?.((la) => {
    return makeData(la, {
      filter: (item) => item.slotBitType == "lhand" && item.type == "armor",
    });
  }),
  meta: (cx) =>
    makeMeta(cx, {
      title: { en: "Shield", ru: "Щиты" },
      canonicalPath: "/items/shield",
    }),
  render: ({ data }) => {
    return (
      <ItemsPage
        config={{
          title: { en: "Shield", ru: "Щиты" },
          menuItemId: "shield",
          shieldStats: true,
        }}
        data={data}
      />
    );
  },
});

const JEWELRY_SLOTS: Partial<Record<Slot, boolean | undefined>> = {
  neck: true,
  ear: true,
  finger: true,
};
$PAGE({
  path: "items/jewelry",
  loader: $LOADER?.((la) => {
    return makeData(la, {
      filter: (item) => !!JEWELRY_SLOTS[item.slotBitType],
    });
  }),
  meta: (cx) =>
    makeMeta(cx, {
      title: { en: "Jewelry", ru: "Бижа" },
      canonicalPath: "/items/jewelry",
    }),
  render: ({ data }) => {
    return (
      <ItemsPage
        config={{
          title: { en: "Jewelry", ru: "Бижа" },
          menuItemId: "jewelry",
          jewelryStats: true,
        }}
        data={data}
      />
    );
  },
});

export default function ItemsPage(props: {
  config: RenderConfig;
  data: LoaderData;
}) {
  const cfg = props.config;
  const data = props.data;

  const t = useT();
  const turl = useTurl();
  const title = t(props.config.title);
  const pager = <Pager paging={data.paging} />;

  const urlSorting = useUrlSorting();

  const breadcrumbList: Array<{ title: string; url: string }> = [];
  let menuItem = MENU_MAP.get(props.config.menuItemId);
  while (menuItem) {
    breadcrumbList.push({
      title: t(menuItem.title),
      url: turl(menuItem.to),
    });

    menuItem = menuItem.parent;
  }
  breadcrumbList.reverse();

  return (
    <div>
      <div className="content">
        <Breadcrumb items={breadcrumbList} noRoot />
        <PageTitle>{title}</PageTitle>
        <Filters />
      </div>
      {pager}
      <div style={{ overflowX: "auto" }}>
        <Table
          data={data.items}
          id={(item) => item.id}
          cols={[
            {
              title: "",
              cell: (item) => (
                <EntityTooltip what={{ t: "item", uname: item.itemName }}>
                  {({ setReference, getReferenceProps }) => (
                    <div
                      ref={setReference}
                      {...getReferenceProps({
                        style: {
                          display: "flex",
                        },
                      })}
                    >
                      <Icon32 icon={item.icon} />
                    </div>
                  )}
                </EntityTooltip>
              ),
              thPadding: "0",
              padding: "8px 0 8px 8px",
              width: 32 + 12,
            },
            {
              title: t({ en: "Name", ru: "Название" }),
              cell: (item) => (
                <>
                  <div className="text-white font-medium">
                    <Link to={turl(`/items/${item.itemName}`)}>
                      {item.name}
                      {item.addName && ` [${item.addName}]`}
                    </Link>
                    {item.crystalType && (
                      <ItemGrade grade={item.crystalType as Grade} />
                    )}
                  </div>
                </>
              ),
            },

            // Weapon stats
            {
              title: t({ en: "Type", ru: "Тип" }),
              cell: (item) => item.weaponType,
              hide: !cfg.weaponStats,
            },
            {
              title: (
                <SortColumnName name="pAtk" by={urlSorting}>
                  {t({ en: "pAtk", ru: "Ф. Атк" })}
                </SortColumnName>
              ),

              cell: (item) => item.physicalDamage,
              hide: !cfg.weaponStats,
            },
            {
              title: (
                <SortColumnName name="mAtk" by={urlSorting}>
                  {t({ en: "mAtk", ru: "М. Атк" })}
                </SortColumnName>
              ),
              cell: (item) => item.magicalDamage,
              hide: !cfg.weaponStats,
            },
            {
              title: "AtkSpeed",
              cell: (item) => item.attackSpeed,
              hide: !cfg.weaponStats,
            },
            {
              title: "SS/SpS",
              cell: (item) => (
                <>
                  {item.soulshotCount} - {item.spiritshotCount}
                </>
              ),
              hide: !cfg.weaponStats,
            },

            // Armor + jewelry
            {
              title: t({ en: "Type", ru: "Тип" }),
              cell: (item) => item.armorType,
              hide: !cfg.armorStats,
            },
            {
              title: t({ en: "Slot", ru: "Слот" }),
              cell: (item) => item.slotBitType,
              hide: !cfg.armorStats && !cfg.jewelryStats,
            },
            {
              title: (
                <SortColumnName name="pDef" by={urlSorting}>
                  {t({ en: "pDef", ru: "Ф. Защ" })}
                </SortColumnName>
              ),
              cell: (item) => item.physicalDefense,
              hide: !cfg.armorStats,
            },
            {
              title: (
                <SortColumnName name="shieldDefense" by={urlSorting}>
                  {t({ en: "pDef", ru: "Ф. Защ" })}
                </SortColumnName>
              ),
              cell: (item) => item.shieldDefense,
              hide: !cfg.shieldStats,
            },
            {
              title: (
                <SortColumnName name="mDef" by={urlSorting}>
                  {t({ en: "mDef", ru: "М. Защ" })}
                </SortColumnName>
              ),
              cell: (item) => item.magicalDefense,
              hide: !cfg.jewelryStats,
            },

            {
              title: (
                <SortColumnName name="cryCount" by={urlSorting}>
                  {t({ en: "Crystals", ru: "Кристаллы" })}
                </SortColumnName>
              ),
              cell: (item) => (
                <>{item.crystalCount !== 0 ? item.crystalCount : "-"}</>
              ),
            },
            {
              title: t({ en: "Weight", ru: "Вес" }),
              cell: (item) => item.weight,
            },
            {
              title: t({ en: "Price", ru: "Цена" }),
              cell: (item) =>
                item.defaultPrice && <AdenaValue value={item.defaultPrice} />,
            },
          ]}
        />
      </div>
      {pager}
    </div>
  );
}

const asSlots = Slot.array().parse;
const asWeaponTypes = WeaponType.array().parse;
const asArmorTypes = ArmorType.array().parse;

function Filters(props: { pages?: ReactNode }) {
  const realm = useRealm();
  const t = useT();
  const urlQuery = useUrlQuery();
  const setUrlQuery = useSetUrlQuery();

  const [filter, setFilter] = useState(() => FILTER.fromUrlQuery(urlQuery));

  return (
    <div style={{ padding: "0 5px" }}>
      <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
        <ControlBox
          title="Grade"
          reset={() => setFilter((f) => ({ ...f, grade: [] }))}
        >
          <MultiSelect
            options={GRADES.map((grade) => ({
              value: grade,
              title: GRADE_NAMES[grade].en,
              color: CrystalColor(grade),
            }))}
            value={filter.grade}
            onChange={(grade) =>
              setFilter((f) => ({ ...f, grade: Grade.array().parse(grade) }))
            }
          />
        </ControlBox>
        <ControlBox
          title="Slot"
          reset={() => setFilter((f) => ({ ...f, slot: [] }))}
        >
          <MultiSelect
            options={SLOTS_BY_GAME_VER[realm.game].map((slot) => ({
              value: slot,
              title: t(SLOT_NAMES[slot]),
            }))}
            value={filter.slot}
            onChange={(slots) =>
              setFilter((f) => ({ ...f, slot: asSlots(slots) }))
            }
          />
        </ControlBox>
        <ControlBox
          title="Weapon type"
          reset={() => setFilter((f) => ({ ...f, weap_type: [] }))}
        >
          <MultiSelect
            options={WEAPON_TYPES_BY_GAME_VER[realm.game].map((wt) => ({
              value: wt,
              title: t(WEAPON_TYPE_NAMES[wt]),
            }))}
            value={filter.weap_type}
            onChange={(wt) =>
              setFilter((f) => ({ ...f, weap_type: asWeaponTypes(wt) }))
            }
          />
        </ControlBox>
        <ControlBox
          title="Armor type"
          reset={() => setFilter((f) => ({ ...f, armr_type: [] }))}
        >
          <MultiSelect
            options={ARMOR_TYPES_BY_GAME_VER[realm.game].map((at) => ({
              value: at,
              title: t(ARMOR_TYPE_NAMES[at]),
            }))}
            value={filter.armr_type}
            onChange={(wt) =>
              setFilter((f) => ({ ...f, armr_type: asArmorTypes(wt) }))
            }
          />
        </ControlBox>
        <ControlBox
          title="Other"
          reset={() => setFilter((f) => ({ ...f, canGet: false }))}
        >
          <CheckBox
            id="canGet"
            checked={filter.canGet ? true : false}
            onChange={(e) =>
              setFilter((f) => ({
                ...f,
                canGet: !f.canGet,
              }))
            }
            label="Can Get"
          />
        </ControlBox>
      </div>

      <div
        style={{
          alignItems: "flex-end",
          display: "flex",
          justifyContent: "space-between",
          paddingTop: 20,
        }}
      >
        <div style={{ padding: "0 0 8px" }}>
          <Button
            onClick={() => {
              setUrlQuery(
                { ...urlQuery, f: FILTER.encode(filter) },
                { preventScrollReset: true }
              );
            }}
          >
            Apply
          </Button>
        </div>
        {props.pages}
      </div>
    </div>
  );
}
